import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Flame, Dumbbell, Trophy, Users } from "lucide-react";
import BrandWordmark from "../components/BrandWordmark";
import BrandFlame from "../components/BrandFlame";
import Slogan, { SLOGANS } from "../components/Slogan";

export default function Landing() {
  return (
    <div className="min-h-screen bg-neutral-950 text-white">
      <header className="border-b border-neutral-900">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BrandFlame className="w-6 h-6" />
            <BrandWordmark size="md" />
          </div>
          <div className="flex items-center gap-3">
            <Link to="/login" data-testid="nav-login-btn">
              <Button variant="ghost" className="text-white hover:text-blue-500 uppercase tracking-wider font-display">Login</Button>
            </Link>
            <Link to="/register" data-testid="nav-register-btn">
              <Button className="bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display">Get Started</Button>
            </Link>
          </div>
        </div>
      </header>

      <section className="relative overflow-hidden">
        <div className="max-w-6xl mx-auto px-6 py-16 lg:py-28 grid lg:grid-cols-2 gap-10 items-center">
          <div className="fade-up">
            <Slogan rotate={3500} className="mb-4 block" testId="landing-slogan" />
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-none tracking-tight uppercase">
              Plan hard. <br/> Train consistent. <br/> Live the lifestyle. <br/> <span className="text-blue-500">Train with Grizz.</span>
            </h1>
            <p className="text-neutral-400 text-base sm:text-lg max-w-xl mt-6 leading-relaxed">
              Build workouts from 70+ gym exercises across Body Building, Strength, CrossFit, HIIT, Yoga & Pilates. Schedule them on a calendar, crush them with a built-in rest timer, and celebrate every PR — together with your gym buddies and live drops from <span className="text-blue-500">@grizz.au</span>.
            </p>
            <div className="flex gap-3 mt-8">
              <Link to="/register" data-testid="hero-cta-register">
                <Button className="bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-12 px-8 text-base">Start Training</Button>
              </Link>
              <Link to="/login" data-testid="hero-cta-login">
                <Button variant="outline" className="bg-transparent border-neutral-700 hover:bg-neutral-900 rounded-none uppercase tracking-wider font-display h-12 px-8 text-base text-white">I have an account</Button>
              </Link>
            </div>
          </div>

          <div className="relative aspect-[4/5] max-w-md mx-auto w-full">
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: "url(https://images.unsplash.com/photo-1709315957145-a4bad1feef28?crop=entropy&cs=srgb&fm=jpg&q=85)" }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-neutral-950 via-neutral-950/40 to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 border border-neutral-700 bg-black/60 backdrop-blur p-4">
              <div className="text-xs tracking-[0.2em] uppercase text-blue-500 font-bold">TODAY'S BEST LIFT</div>
              <div className="font-display text-3xl mt-1">142 kg · Deadlift</div>
              <div className="text-neutral-400 text-sm">Personal Record · +7kg</div>
            </div>
          </div>
        </div>
      </section>

      <section className="border-y border-neutral-900 bg-black overflow-hidden">
        <div className="py-4 whitespace-nowrap" data-testid="slogan-ribbon">
          {/* duplicate the track twice for seamless loop */}
          <div className="marquee-track font-display text-2xl uppercase tracking-widest">
            {[...SLOGANS, ...SLOGANS].map((s, i) => (
              <span key={i} className="flex items-center gap-12 pr-12">
                <span className={i % 2 === 0 ? "text-blue-500" : "text-neutral-500"}>{s}</span>
                <span className="text-neutral-700">·</span>
              </span>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-neutral-900 bg-black">
        <div className="max-w-6xl mx-auto px-6 py-14 grid md:grid-cols-4 gap-6">
          {[
            { icon: Dumbbell, title: "70+ Exercises", desc: "Body Building · Strength · CrossFit · HIIT · Yoga · Pilates" },
            { icon: Flame, title: "Active Timer", desc: "Customizable rest between sets with 5 alarm sounds" },
            { icon: Trophy, title: "PRs & Levels", desc: "Wood to Diamond — earn progression for consistency" },
            { icon: Users, title: "Gym Buddies", desc: "Add friends, chat, share workouts and celebrations" },
          ].map((f, i) => (
            <div key={i} className="border border-neutral-800 p-6 hover:border-blue-500 transition-colors">
              <f.icon className="w-8 h-8 text-blue-500 mb-4" strokeWidth={2} />
              <div className="font-display text-xl tracking-wide uppercase">{f.title}</div>
              <div className="text-sm text-neutral-400 mt-2 leading-relaxed">{f.desc}</div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
